import { apiClient } from '../utils/apiClient';

class CollectionService {
  // Get all collections (optionally filtered by workspace)
  async getCollections(workspaceId) {
    if (!workspaceId) {
      throw new Error('workspace_id is required');
    }
    try {
      const response = await apiClient.get('/collections', {
        params: { workspace_id: workspaceId },
      });
      return response.data;
    } catch (error) {
      console.error('Failed to fetch collections:', error);
      const msg =
        error.response?.data?.message ||
        error.response?.data?.error ||
        'Failed to fetch collections';
      throw new Error(msg);
    }
  }

  // Get a specific collection
  async getCollection(collectionId) {
    try {
      const response = await apiClient.get(`/collections/${collectionId}`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch collection:', error);
      throw new Error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          'Failed to fetch collection'
      );
    }
  }

  /**
   * Import whole collections — each with all its requests — in ONE request.
   *
   * The old path called createCollection once and then createRequest once per
   * request, sequentially: 327 round-trips for a real Postman export, about a
   * minute behind a spinner with no progress and no cancel. Users reported it as
   * the app freezing, and it only happened on cloud workspaces because the local
   * path is an in-memory map.
   *
   * The server does it in one transaction, so a malformed request no longer
   * leaves a half-imported collection behind — the old client tried to clean that
   * up by DELETEing the collection it had just made, which is not atomic and
   * failed silently if the app was closed.
   */
  async importCollections(workspaceId, collections) {
    try {
      // 60 s, not the client-wide 10 s: an import body can be megabytes on a
      // slow uplink, and a client-side timeout on a call the server COMMITS
      // reads as "failed" — the retry then imports everything twice.
      const response = await apiClient.post(
        '/collections/import',
        { workspace_id: workspaceId, collections },
        { timeout: 60_000 }
      );
      return response.data?.collections || [];
    } catch (error) {
      console.error('Failed to import collections:', error);
      const err = new Error(
        error.response?.data?.error || error.response?.data?.message || 'Failed to import collections'
      );
      // Preserved so the caller can spot a 402 and open the upgrade modal, the
      // same way createCollection does.
      err.response = error.response;
      throw err;
    }
  }

  // Create a new collection
  async createCollection(collectionData) {
    try {
      const response = await apiClient.post('/collections', collectionData);
      return response.data;
    } catch (error) {
      console.error('Failed to create collection:', error);
      const err = new Error(
        error.response?.data?.error || error.response?.data?.message || 'Failed to create collection'
      );
      // Preserve the Axios response so callers can detect plan-limit 402s
      // (error.response.data.upgrade_required) and open the upgrade modal.
      err.response = error.response;
      throw err;
    }
  }

  // Update a collection
  async updateCollection(collectionId, collectionData) {
    try {
      const response = await apiClient.put(`/collections/${collectionId}`, collectionData);
      return response.data;
    } catch (error) {
      console.error('Failed to update collection:', error);
      throw new Error(error.response?.data?.message || 'Failed to update collection');
    }
  }

  // Delete a collection
  async deleteCollection(collectionId) {
    try {
      const response = await apiClient.delete(`/collections/${collectionId}`);
      return response.data;
    } catch (error) {
      console.error('Failed to delete collection:', error);
      throw new Error(error.response?.data?.message || 'Failed to delete collection');
    }
  }

  // Share collection with team
  async shareWithTeam(collectionId, teamData) {
    try {
      const response = await apiClient.post(`/collections/${collectionId}/share-team`, teamData);
      return response.data;
    } catch (error) {
      console.error('Failed to share collection:', error);
      throw new Error(error.response?.data?.message || 'Failed to share collection');
    }
  }

  // Unshare collection from team
  async unshareFromTeam(collectionId, teamId) {
    try {
      const response = await apiClient.delete(`/collections/${collectionId}/teams/${teamId}`);
      return response.data;
    } catch (error) {
      console.error('Failed to unshare collection:', error);
      throw new Error(error.response?.data?.message || 'Failed to unshare collection');
    }
  }

  // Get collection variable scopes
  async getVariableScopes(collectionId) {
    try {
      const response = await apiClient.get(`/collections/${collectionId}/variables`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch collection variable scopes:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch collection variable scopes');
    }
  }

  // Update collection variable scopes
  async updateVariableScopes(collectionId, payload) {
    try {
      const response = await apiClient.put(`/collections/${collectionId}/variables`, payload);
      return response.data;
    } catch (error) {
      console.error('Failed to update collection variable scopes:', error);
      throw new Error(error.response?.data?.message || 'Failed to update collection variable scopes');
    }
  }

  // Clear any cached data (for logout cleanup)
  clearCache() {
    // Clear any cached collection data if you implement caching in the future
    console.log('CollectionService cache cleared');
  }
}

export const collectionService = new CollectionService();
